import React, { useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAppContext } from '../../context/AppContext';

const AgencyLayout = () => {
  const { agencyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { agencies } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const agency = agencies.find(item => item.id === agencyId);
  const isAgencyHome = location.pathname === `/portal/${agencyId}`;
  const isAuthenticated = Cookies.get(`shift_auth_${agencyId}`) === 'true';

  if (!isAgencyHome && !isAuthenticated) {
    return <Navigate to={`/portal/${agencyId}`} replace />;
  }

  if (!isAuthenticated) return <Outlet />;

  const navItems = [
    { label: 'Accounts', path: `/portal/${agencyId}`, icon: FiIcons.FiUsers, end: true },
    { label: 'Task Log', path: `/portal/${agencyId}/task-log`, icon: FiIcons.FiClock },
  ];

  const logout = () => {
    Cookies.remove(`shift_auth_${agencyId}`);
    setMenuOpen(false);
    navigate(`/portal/${agencyId}`);
  };

  const sidebar = (
    <aside className="w-64 h-full bg-slate-950 text-slate-200 border-r border-slate-800 flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="w-36 mb-6">
          <img
            src="https://media-manager-c.questera.ai/greta-media/184c9c9a291724797b8ad828c55b10701c0f1ac03d6d548cee29a666ff97a2e9df5b13aa801d7dcc8d672f63188942d5/images/aW1hZ2UvcG5n/c78450fbbdba9eba333bc0f72a71b422.png"
            alt="Shift Creative"
            className="w-full object-contain"
          />
        </div>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: agency?.color || '#14b8a6' }} />
          <span className="font-bold text-sm text-white truncate">{agency?.name || 'Agency Portal'}</span>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
              }`
            }
          >
            <SafeIcon icon={item.icon} className="text-lg" />
            <span className="font-bold text-sm">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-900 hover:text-red-400 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiLogOut} className="text-lg" />
          <span className="font-bold text-sm">Log Out</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 lg:flex">
      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0">{sidebar}</div>

      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between p-4 bg-slate-900 border-b border-slate-800">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 text-slate-400 hover:text-white"
          aria-label="Open agency navigation"
        >
          <SafeIcon icon={FiIcons.FiMenu} className="text-2xl" />
        </button>
        <span className="font-bold text-sm text-white truncate">{agency?.name || 'Agency Portal'}</span>
        <span className="w-10" />
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-slate-950/80"
            onClick={() => setMenuOpen(false)}
            aria-label="Close agency navigation"
          />
          <div className="relative h-full w-64">{sidebar}</div>
        </div>
      )}

      <main className="min-w-0 flex-1 lg:ml-64">
        <Outlet />
      </main>
    </div>
  );
};

export default AgencyLayout;